import { useState, useRef, memo, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Popover,
  Select,
  Stack,
  ButtonBase,
  SxProps,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { SelectRenderValueElem } from 'components/Inputs/Dropdown/SelectDropdown/SelectRenderValueElem';
import { DropdownHeader } from 'components/Inputs/Dropdown/DropdownHeader';
import {
  getSelectSx,
  getCloseButtonSx,
  getFooterContainerSx,
  getFormControlSx,
} from '../dropdownConsts';
import { bloxValues } from 'themes/constants';

dayjs.extend(utc);
dayjs.extend(timezone);

interface SingleDateDropdownProps {
  label?: string;
  value: string | null;
  onChange: (value: string | null) => void;
  placeholder?: string;
  disabled?: boolean;
  /** default: false - if true, displays a MUI InputLabel on the FormControl with shrink behavior */
  showFormControlLabel?: boolean;
  /** Optional FormControl sx overrides */
  formControlSx?: SxProps<Theme>;
  /** Optional Select sx overrides */
  selectSx?: SxProps<Theme>;
  /**
   * IANA timezone string (e.g., 'America/Los_Angeles') for displaying
   * and interpreting dates. If not provided, uses local browser timezone.
   */
  timezone?: string;
}

export const SingleDateDropdown = memo(
  ({
    label,
    value,
    onChange,
    placeholder,
    disabled = false,
    showFormControlLabel = false,
    formControlSx,
    selectSx,
    timezone: tz,
  }: SingleDateDropdownProps) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const anchorRef = useRef<HTMLElement | null>(null);

    // Parse value with timezone awareness
    const parseWithTimezone = useCallback(
      (dateValue: string | null) => {
        if (!dateValue || dateValue === '') return null;
        const parsed = dayjs(dateValue);
        return tz ? parsed.tz(tz) : parsed;
      },
      [tz],
    );

    const initialDayjs = parseWithTimezone(value);
    const [tempDate, setTempDate] = useState(initialDayjs);

    const isRemove = value === '';
    const isDirty = (tempDate ? tempDate.toISOString() : null) !== value;
    const showClear = value !== null && value !== undefined;

    const resolvedLabel = label ?? t('blox.general.select_date');
    const resolvedPlaceholder = showFormControlLabel
      ? placeholder
      : (placeholder ?? t('blox.general.select_date'));

    useEffect(() => {
      if (open) {
        const parsed = parseWithTimezone(value);
        setTempDate(parsed);
      }
    }, [open, value, parseWithTimezone]);

    const handleOpen = () => setOpen(true);
    const handleClose = () => setOpen(false);

    const handleSaveOrClear = () => {
      if (!tempDate || !tempDate.isValid()) {
        onChange(null); // clear or remove
      } else {
        onChange(tempDate.toISOString());
      }
      handleClose();
    };

    const selectedChoices = isRemove
      ? [{ label: t('blox.actions.remove'), value: '' }]
      : value
        ? [{ label: (tz ? dayjs(value).tz(tz) : dayjs(value)).format('ll LT'), value }]
        : [];

    const showClearButton = isRemove || (!isDirty && showClear);

    const handleClear = () => {
      setTempDate(null);
      onChange(null);
      handleClose();
    };

    return (
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <FormControl
          fullWidth
          size="small"
          variant="outlined"
          disabled={disabled}
          sx={{
            ...getFormControlSx(theme, open),
            ...(typeof formControlSx === 'function'
              ? formControlSx(theme)
              : formControlSx),
          }}
        >
          {showFormControlLabel && <InputLabel shrink>{resolvedLabel}</InputLabel>}
          <Select
            data-testid="single-date-dropdown-select"
            ref={anchorRef}
            value="selected"
            displayEmpty
            onOpen={handleOpen}
            open={open}
            MenuProps={{ open: false }}
            label={showFormControlLabel ? resolvedLabel : undefined}
            renderValue={() => (
              <SelectRenderValueElem
                selectedChoices={selectedChoices}
                placeholderText={resolvedPlaceholder ?? ''}
                renderValueDisplay="chips"
                chipColor={isRemove ? 'error' : value ? 'success' : undefined}
              />
            )}
            sx={{
              ...getSelectSx(theme),
              ...(typeof selectSx === 'function' ? selectSx(theme) : selectSx),
            }}
          >
            <MenuItem
              value="selected"
              style={{ display: 'none' }}
            />
          </Select>
          <Popover
            anchorEl={anchorRef.current}
            open={open}
            onClose={handleClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          >
            <Box>
              <DropdownHeader
                label={resolvedLabel}
                disabled={disabled}
                valueIsEmpty={value !== ''}
                onRemove={() => {
                  onChange('');
                  handleClose();
                }}
                alwaysShowRemove={true}
              />
              <Stack
                spacing={1}
                sx={{
                  p: 2,
                }}
              >
                <DateTimePicker
                  data-testid="single-date-dropdown-datepicker"
                  value={tempDate}
                  onChange={(newDate) => {
                    if (newDate?.isValid()) setTempDate(newDate);
                  }}
                  slotProps={{
                    textField: { size: 'small', fullWidth: true, variant: 'outlined' },
                  }}
                  sx={{
                    '& .MuiSvgIcon-root': {
                      fontSize: bloxValues.regularFontSizeRem, // scale down the calendar icon
                    },
                    '& .MuiInputBase-root, & .MuiFormControlLabel-label': {
                      fontSize: bloxValues.smallFontSizeRem,
                    },
                  }}
                />
              </Stack>
              <Stack
                sx={{
                  gridArea: 'selectfooter',
                  display: 'grid',
                  gridTemplateColumns: 'auto auto',
                  gridTemplateAreas: `"updatebtn closebtn"`,
                  justifyContent: 'end',
                  alignItems: 'center',
                  ...getFooterContainerSx(theme, true),
                }}
              >
                <Button
                  data-testid="single-date-dropdown-update-btn"
                  size="small"
                  variant="contained"
                  onClick={showClearButton ? handleClear : handleSaveOrClear}
                  disabled={!isDirty && !showClear}
                  color={showClearButton ? 'success' : 'primary'}
                  sx={{
                    gridArea: 'updatebtn',
                    color: theme.palette.common.white,
                    borderRadius: 0,
                    px: 2,
                    height: '100%',
                  }}
                  disableElevation
                >
                  {showClearButton ? t('blox.actions.clear') : t('blox.actions.update')}
                </Button>

                <ButtonBase
                  data-testid="single-date-dropdown-close-btn"
                  onClick={handleClose}
                  sx={{
                    gridArea: 'closebtn',
                    ...getCloseButtonSx(theme),
                  }}
                >
                  {t('blox.actions.close')}
                </ButtonBase>
              </Stack>
            </Box>
          </Popover>
        </FormControl>
      </LocalizationProvider>
    );
  },
);

SingleDateDropdown.displayName = 'SingleDateDropdown';
